import { Box, Backdrop, CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import LogIn from './LogIn';
import SignUp from './SignUp';
import VerifyEmail from "./VerifyEmail";
import JoinConf from "./JoinConf";
import Planet from '../widgets/planet/Planet';
import { getQuestions, submitResponses } from '../../utils/mutations/form';

import type { User, Question } from '../../utils/types';

import ProgressiveImage from 'react-progressive-graceful-image';
import Footer from "../confPages/menuBars/Footer";
import UserForm from "../widgets/UserForm";

//  Flowchart of the Home component
//  --------------------------------
//        /-> SignUp -> VerifyEmail -> JoinConf -> PaymentWidget -> FormWidget -> Portal 
//       /                                                                     \                        
// Home |                                                                        }> OR hyls page
//       \                                                                     /                       
//        \-> Login -----------------> JoinConf -> PaymentWidget -> FormWidget -> Portal
// --------------------------------

interface ProgressiveBackgroundBoxProps {
  children: React.ReactNode;
  backgroundImage: string;
  placeholderImage: string;
}

const ProgressiveBackgroundBox: React.FC<ProgressiveBackgroundBoxProps> = ({ children, backgroundImage, placeholderImage }) => {
  return (
    <ProgressiveImage
      src={backgroundImage}
      placeholder={placeholderImage}
    >
      {(src: any, loading: any) => (
        <Box
          sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundImage: `url(${src})`, // Use the src provided by ProgressiveImage
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white',
            flexDirection: 'row',
            display: 'flex',
            alignItems: 'center',
            opacity: loading ? 0.5 : 1, // Adjust opacity during loading
            position: 'relative',
          }}
        >
          {children}
        </Box>
      )}
    </ProgressiveImage>
  );
};

interface HomeProps {
  user: User | null;
  emailVerified: boolean;
  setUser: (user: User) => void;
  setEmailVerified: (emailVerified: boolean) => void;
}

const Home: React.FC<HomeProps> = ({ user, emailVerified, setUser, setEmailVerified }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width:850px)');
  const [pageState, setPageState] = useState<number>(0);
  // 0: loading 1: login, 2: signup, 3: verify email, 4: join conf/payment widget, 5: form widget

  const [questions, setQuestions] = useState<Question[]>([]);

  const fetchQuestionsAndProcess = useCallback(async (confCode: string) => {
    try {
      setPageState(0); // Loading state
      
      // Get questions and capture the response directly
      const fetchedQuestions = await getQuestions(confCode);
      setQuestions(fetchedQuestions);
  
      // Use the fetched data directly instead of relying on state
      if (fetchedQuestions.length === 0) {
        if (!confCode || !user?.uid) {
          throw new Error('Conference code or user missing');
        }
        
        // call submitResponses with empty object to mark form as submitted
        await submitResponses(confCode, user.uid, null);
        window.location.reload();
      }
  
      setPageState(5);
    } catch (error) {
      console.error('Error in question processing:', error);
      // Add error state handling
      setPageState(1); // or whatever error state you want to show
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user) {
      if(!isSignUp) {
        setPageState(1); // 1: login
      } else {
        setPageState(2); // 2: signup
      }
    } else if(!emailVerified) {
      setPageState(3);
    } else {
      // Always show join form on home page, even if user has an old conferenceCode
      // This allows past participants to join a new conference
      setPageState(4);
    }
  }
  , [user, isSignUp, emailVerified]);

  // When a user is verified and has a conference but has not submitted the form,
  // load questions and move to the form step.
  useEffect(() => {
    if (user && emailVerified && user.conferenceCode && !user.formSubmitted) {
      fetchQuestionsAndProcess(user.conferenceCode);
    }
  }, [user, emailVerified, fetchQuestionsAndProcess]);


  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
    }}>
    <ProgressiveBackgroundBox
      backgroundImage="art/tower-homepage.png"
      placeholderImage="art/tower-homepage-compressed.png"
    >
    
      {!isSmallScreen && (
        <Box sx={{
          width: '50%',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center', 
          alignItems: 'center',
        }}>
          <Planet />
        </Box>
      )}

      <Box sx={{
        width: isSmallScreen ? '100%' : '50%',
        display: 'flex',
        justifyContent: 'center', 
        alignItems: 'center',
      }}>
        <Box
          sx={{
            borderRadius: '20px',
            marginY: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '25px 5px',
            minWidth: '350px',
            maxHeight: '90vh',
            width: '60%',
            overflow: 'auto',
            position: 'relative', // Required for absolute positioning of pseudo-element
            '::before': { // this `::before` is how we can blur and still allow the payment pop up(!)
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: 'blur(4px) saturate(180%)', // Apply blur filter here
              zIndex: 0, // Ensure it's behind the content
            },
          }}
        >
          <img src="art/HPAIR Logo Banner (Black).png" alt="HPAIR Logo" style={{ marginBottom: '10px', width: '300px', zIndex: 1 }} /> 
          <Box sx={{
            zIndex: 1, // Ensure it's in front of the pseudo-element
          }}>
            <Backdrop
              open={pageState === 0}
              sx={{
                color: '#fff',
                zIndex: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            {/* Conditional rendering based on user state */}
            {pageState === 1 && <LogIn setIsSignUp={setIsSignUp} />}
            {pageState === 2 && <SignUp setIsSignUp={setIsSignUp} setUser={setUser} />}
            {pageState === 3 && <VerifyEmail setEmailVerified={setEmailVerified} />}
            {pageState === 4 && user && <JoinConf user={user} />}
            {pageState === 5 && user && user.conferenceCode && questions.length > 0 && 
              <UserForm user={user} confCode={user.conferenceCode} 
                        questions={questions} onSubmit={()=>{window.location.reload()}} />
            }
          </Box>
        </Box>
      </Box>
    </ProgressiveBackgroundBox>
    <Footer />
    </Box>
  );
};

export default Home;
